import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { LogistikProductSet } from '../LogistikProduct.js';
import { LogistikProductFilter } from '../LogistikProductFilter.js';

const resolveSubscribeLogistikProductSet =
  (skip: number, limit: number, filter: LogistikProductFilter, service: string | undefined = undefined) =>
  (gqlFields: BaseQLSelectionSet<LogistikProductSet>) =>
    from(publishFilter(LogistikProductSet._class)).pipe(
      switchMap(publishFilter =>
        watchQuery<LogistikProductSet>(
          LogistikProductSet._collectionName,
          publishFilter
            ? {
                $and: [
                  publishFilter,
                  { isPublished: { $ne: false } },
                  { _class: { $in: baseqlMetaData.entities.validSubclasses(LogistikProductSet) } },
                  ...(service ? [{ services: { $elemMatch: { $eq: service } } }] : []),
                ],
              }
            : {
                isPublished: { $ne: false },
                _class: { $in: baseqlMetaData.entities.validSubclasses(LogistikProductSet) },
                ...(service ? { services: { $elemMatch: { $eq: service } } } : {}),
              },
          { sort: { _id: -1 } },
        ),
      ),
      catchError(err => {
        logger.error({ err }, 'Fehler in subscribeLogistikProductSetsPaginated');
        return of([] as LogistikProductSet[]);
      }),
    );

const subscribeLogistikProductSetsPaginated = registerSubscription({
  type: () => [LogistikProductSet],
  operationName: 'subscribeLogistikProductSetsPaginated',
  options: {},
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => LogistikProductFilter, name: 'filter' },
  ],
  resolve: resolveSubscribeLogistikProductSet,
});

const subscribeLogistikProductSetsForService = registerSubscription({
  type: () => [LogistikProductSet],
  operationName: 'subscribeLogistikProductSetsForService',
  options: {},
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => LogistikProductFilter, name: 'filter' },
  ],
  resolve: (skip: number, limit: number, filter: LogistikProductFilter) =>
    resolveSubscribeLogistikProductSet(skip, limit, filter, projectConfig.get('SERVICE_NAME').toLowerCase()),
});

const subscribePublishedLogistikProductSetCount = registerSubscription({
  type: () => Int,
  operationName: 'subscribePublishedLogistikProductSetCount',
  options: {},
  params: [],
  resolve: () => {
    return () => {
      return watchQuery(
        LogistikProductSet._collectionName,
        {
          _class: { $in: baseqlMetaData.entities.validSubclasses(LogistikProductSet) },
          isPublished: { $ne: false },
        },
        { projection: { _id: 1 } },
      ).pipe(map(lpss => lpss.length));
    };
  },
});

export namespace LogistikProductSetAPI {
  export const subscribePaginated = subscribeLogistikProductSetsPaginated;
  export const subscribeForService = subscribeLogistikProductSetsForService;
  export const count = subscribePublishedLogistikProductSetCount;
}
